// Cyber Invaders canvas: starfield, labelled ships in independently drifting
// rows, the player cannon, bullets and effects. Rules live in logic.ts; the
// engine only reports which ship was shot or reached the line.

import type { ShipDef, WaveDef } from "./logic";

/** Logical playfield width; height follows the canvas aspect. */
export const LW = 1280;

const SHIP_COLOR = "#c58bff";
const PLAYER_COLOR = "#48f07a";
const LABEL_FONT = "700 18px ui-monospace, 'Cascadia Mono', Consolas, 'Courier New', monospace";
const FLOAT_FONT = "800 22px ui-monospace, 'Cascadia Mono', Consolas, 'Courier New', monospace";

const INV_A = [
  "..X.....X..",
  "...X...X...",
  "..XXXXXXX..",
  ".XX.XXX.XX.",
  "XXXXXXXXXXX",
  "X.XXXXXXX.X",
  "X.X.....X.X",
  "...XX.XX...",
];
const INV_B = [
  "..X.....X..",
  "X..X...X..X",
  "X.XXXXXXX.X",
  "XXX.XXX.XXX",
  "XXXXXXXXXXX",
  ".XXXXXXXXX.",
  "..X.....X..",
  ".X.......X.",
];
const CANNON = [
  "......X......",
  ".....XXX.....",
  ".....XXX.....",
  ".XXXXXXXXXXX.",
  "XXXXXXXXXXXXX",
  "XXXXXXXXXXXXX",
  "XXXXXXXXXXXXX",
  "XXXXXXXXXXXXX",
];

const SHIP_PX = 3.6;
const SHIP_W = 11 * SHIP_PX;
const SHIP_H = 8 * SHIP_PX;
const LABEL_GAP = 5;
const PLATE_H = 26;
const ROW_H = SHIP_H + LABEL_GAP + PLATE_H;
const ROW_SPACING = ROW_H + 36;
const SHIP_GAP = 30;
const MARGIN = 36;
const CANNON_PX = 4;
const CANNON_W = 13 * CANNON_PX;
const MAX_BULLETS = 2;
const BULLET_SPEED = 1100;
const FIRE_COOLDOWN = 0.22;
const PLAYER_SPEED = 560;
const FOLLOW_SPEED = 1400;

type ShipState = "alive" | "boom" | "warp" | "breach" | "pass" | "gone";

interface EShip {
  def: ShipDef;
  ox: number;
  w: number;
  state: ShipState;
  /** Seconds since the ship was resolved. */
  t: number;
  x: number;
  y: number;
}

interface Row {
  y: number;
  x: number;
  vx: number;
  width: number;
  ships: EShip[];
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
  size: number;
}

interface Floater {
  x: number;
  y: number;
  text: string;
  color: string;
  t: number;
}

export interface EngineEvents {
  onHit(def: ShipDef, x: number, y: number): void;
  onPass(def: ShipDef, x: number, y: number): void;
  onCleared(): void;
}

export type EngineMode = "demo" | "play" | "idle";

export class InvadersEngine {
  private ctx: CanvasRenderingContext2D;
  private raf = 0;
  private last = 0;
  private time = 0;
  private lh = 720;
  private mode: EngineMode = "demo";
  private paused = false;
  private halted = false;
  private reduced = false;
  private rows: Row[] = [];
  private speed = 18;
  private bullets: { x: number; y: number }[] = [];
  private particles: Particle[] = [];
  private floaters: Floater[] = [];
  private stars: { x: number; y: number; s: number; p: number }[] = [];
  private playerX = LW / 2;
  private keys = { left: false, right: false };
  private targetX: number | null = null;
  private cooldown = 0;
  private shakeT = 0;
  private playTopFrac = 0.22;
  private lastResolve = 0;
  private clearedSent = false;

  constructor(private canvas: HTMLCanvasElement, private events: EngineEvents) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d canvas unavailable");
    this.ctx = ctx;
    for (let i = 0; i < 120; i++) {
      this.stars.push({ x: Math.random() * LW, y: Math.random(), s: Math.random() < 0.15 ? 2.4 : 1.4, p: Math.random() * 6 });
    }
    this.resize();
  }

  // ---- geometry --------------------------------------------------------

  private get playTopY() {
    return this.playTopFrac * this.lh;
  }
  private get dangerY() {
    return this.lh * 0.775;
  }
  private get playerY() {
    return this.lh * 0.8;
  }

  resize() {
    const r = this.canvas.getBoundingClientRect();
    const cw = Math.max(1, r.width);
    const ch = Math.max(1, r.height);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.round(cw * dpr);
    const h = Math.round(ch * dpr);
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
    this.lh = (LW * ch) / cw;
  }

  /** Top of the playfield as a fraction of the height (below the rule box). */
  setPlayTop(frac: number) {
    this.playTopFrac = Math.max(0.1, Math.min(0.45, frac));
  }

  setReducedMotion(on: boolean) {
    this.reduced = on;
  }

  setPaused(on: boolean) {
    this.paused = on;
    if (on) {
      this.keys.left = false;
      this.keys.right = false;
    }
  }

  // ---- lifecycle -------------------------------------------------------

  start() {
    if (this.raf) return;
    this.last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
      this.last = now;
      this.update(dt);
      this.draw();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  setMode(mode: EngineMode) {
    this.mode = mode;
    this.halted = false;
    this.bullets = [];
    this.keys.left = this.keys.right = false;
    this.targetX = null;
    if (mode === "demo") this.loadDemo();
    if (mode === "idle") {
      this.rows = [];
      this.floaters = [];
    }
  }

  /** Freeze the ships after the last life is lost; effects keep playing. */
  halt() {
    this.halted = true;
    this.bullets = [];
  }

  private measure(label: string) {
    this.ctx.font = LABEL_FONT;
    return this.ctx.measureText(label).width;
  }

  loadWave(wave: WaveDef) {
    this.mode = "play";
    this.halted = false;
    this.clearedSent = false;
    this.bullets = [];
    this.particles = [];
    this.floaters = [];
    this.speed = wave.speed;
    this.playerX = LW / 2;
    this.targetX = null;
    this.rows = [];
    const per = Math.max(1, wave.perRow);
    for (let i = 0; i * per < wave.ships.length; i++) {
      const defs = wave.ships.slice(i * per, i * per + per);
      let ox = 0;
      const ships: EShip[] = defs.map((def) => {
        const w = Math.max(SHIP_W + 16, this.measure(def.label) + 20);
        const s: EShip = { def, ox, w, state: "alive", t: 0, x: 0, y: 0 };
        ox += w + SHIP_GAP;
        return s;
      });
      const width = ox - SHIP_GAP;
      const room = Math.max(0, LW - 2 * MARGIN - width);
      const dir = i % 2 === 0 ? 1 : -1;
      this.rows.push({
        y: this.playTopY + 6 - i * ROW_SPACING,
        x: MARGIN + Math.random() * room,
        vx: dir * (24 + Math.random() * 26),
        width,
        ships,
      });
    }
  }

  private loadDemo() {
    this.rows = [];
    this.particles = [];
    this.floaters = [];
    for (let i = 0; i < 4; i++) {
      const ships: EShip[] = [];
      for (let k = 0; k < 6; k++) {
        ships.push({
          def: { id: `demo-${i}-${k}`, label: "", risky: false, category: "file", lesson: "" },
          ox: k * (SHIP_W + 46),
          w: SHIP_W,
          state: "alive",
          t: 0,
          x: 0,
          y: 0,
        });
      }
      const width = 6 * (SHIP_W + 46) - 46;
      this.rows.push({
        y: this.lh * (0.18 + i * 0.13),
        x: MARGIN + Math.random() * (LW - 2 * MARGIN - width),
        vx: (i % 2 ? -1 : 1) * (40 + i * 12),
        width,
        ships,
      });
    }
  }

  // ---- input -----------------------------------------------------------

  setKey(dir: "left" | "right", down: boolean) {
    this.keys[dir] = down;
    if (down) this.targetX = null;
  }

  /** Follow a pointer, in logical x. */
  setTargetX(x: number | null) {
    this.targetX = x == null ? null : Math.max(CANNON_W / 2, Math.min(LW - CANNON_W / 2, x));
  }

  /** Canvas client x to logical x. */
  toLogicalX(clientX: number) {
    const r = this.canvas.getBoundingClientRect();
    return ((clientX - r.left) / Math.max(1, r.width)) * LW;
  }

  /** Returns true when a bullet left the cannon. */
  fire(): boolean {
    if (this.mode !== "play" || this.paused || this.halted) return false;
    if (this.cooldown > 0 || this.bullets.length >= MAX_BULLETS) return false;
    this.bullets.push({ x: this.playerX, y: this.playerY - 4 });
    this.cooldown = FIRE_COOLDOWN;
    return true;
  }

  shake() {
    if (!this.reduced) this.shakeT = 0.35;
  }

  float(x: number, y: number, text: string, kind: "good" | "bad" | "info") {
    const color = kind === "good" ? "#48f07a" : kind === "bad" ? "#ff9a4a" : "#ffe03a";
    this.floaters.push({ x, y, text, color, t: 0 });
  }

  // ---- debug (only used by window.__cinvDebug) ---------------------------

  debugShips() {
    return this.rows.flatMap((r) =>
      r.ships.map((s) => ({ label: s.def.label, risky: s.def.risky, state: s.state, x: r.x + s.ox, y: r.y })),
    );
  }

  /** Resolve every remaining ship: risky shot, safe passed, except `mistakes` risky ships let through. */
  debugFinish(mistakes = 0) {
    let left = mistakes;
    for (const row of this.rows) {
      for (const s of row.ships) {
        if (s.state !== "alive") continue;
        if (s.def.risky && left > 0) {
          left -= 1;
          this.resolvePass(row, s);
        } else if (s.def.risky) {
          this.resolveHit(row, s);
        } else {
          this.resolvePass(row, s);
        }
        if (this.halted) return;
      }
    }
  }

  // ---- simulation --------------------------------------------------------

  private resolveHit(row: Row, s: EShip) {
    const cx = row.x + s.ox + s.w / 2;
    const cy = row.y + SHIP_H / 2;
    s.state = s.def.risky ? "boom" : "warp";
    s.t = 0;
    s.x = row.x + s.ox;
    s.y = row.y;
    this.lastResolve = this.time;
    if (s.def.risky) this.explode(cx, cy);
    else this.shimmer(cx, cy);
    this.events.onHit(s.def, cx, cy);
  }

  private resolvePass(row: Row, s: EShip) {
    const cx = row.x + s.ox + s.w / 2;
    s.state = s.def.risky ? "breach" : "pass";
    s.t = 0;
    s.x = row.x + s.ox;
    s.y = row.y;
    this.lastResolve = this.time;
    if (s.def.risky) this.breach(cx, this.dangerY);
    this.events.onPass(s.def, cx, this.dangerY - 26);
  }

  private explode(x: number, y: number) {
    const colors = ["#ffffff", "#ffe03a", "#ffb03a", SHIP_COLOR];
    const n = this.reduced ? 14 : 30;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 80 + Math.random() * 260;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 0,
        max: 0.5 + Math.random() * 0.4,
        color: colors[i % colors.length],
        size: 3 + Math.random() * 4,
      });
    }
  }

  private shimmer(x: number, y: number) {
    for (let i = 0; i < 18; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 60,
        y: y + (Math.random() - 0.5) * 30,
        vx: (Math.random() - 0.5) * 30,
        vy: -60 - Math.random() * 90,
        life: 0,
        max: 0.6 + Math.random() * 0.4,
        color: i % 2 ? "#7fe8ff" : "#ffffff",
        size: 2 + Math.random() * 2,
      });
    }
  }

  private breach(x: number, y: number) {
    for (let i = 0; i < 16; i++) {
      const a = Math.PI + Math.random() * Math.PI;
      const sp = 60 + Math.random() * 160;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 0,
        max: 0.45 + Math.random() * 0.3,
        color: i % 2 ? "#ff9a4a" : "#ffe03a",
        size: 3 + Math.random() * 3,
      });
    }
  }

  private update(dt: number) {
    this.time += dt;
    if (this.paused) return;
    this.shakeT = Math.max(0, this.shakeT - dt);
    this.cooldown = Math.max(0, this.cooldown - dt);

    for (const p of this.particles) {
      p.life += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 140 * dt;
    }
    this.particles = this.particles.filter((p) => p.life < p.max);
    for (const f of this.floaters) {
      f.t += dt;
      f.y -= 38 * dt;
    }
    this.floaters = this.floaters.filter((f) => f.t < 1.3);
    for (const row of this.rows) for (const s of row.ships) if (s.state !== "alive" && s.state !== "gone") s.t += dt;

    if (this.mode === "demo") {
      for (const row of this.rows) {
        row.x += row.vx * dt;
        if (row.x < MARGIN || row.x + row.width > LW - MARGIN) {
          row.vx = -row.vx;
          row.x = Math.max(MARGIN, Math.min(LW - MARGIN - row.width, row.x));
        }
        row.y += 10 * dt;
        if (row.y > this.lh * 0.72) row.y = this.lh * 0.14;
      }
      return;
    }
    if (this.mode !== "play") return;

    // player
    if (!this.halted) {
      const dir = (this.keys.right ? 1 : 0) - (this.keys.left ? 1 : 0);
      if (dir !== 0) this.playerX += dir * PLAYER_SPEED * dt;
      else if (this.targetX != null) {
        const d = this.targetX - this.playerX;
        const step = FOLLOW_SPEED * dt;
        this.playerX += Math.abs(d) <= step ? d : Math.sign(d) * step;
      }
      this.playerX = Math.max(CANNON_W / 2 + 8, Math.min(LW - CANNON_W / 2 - 8, this.playerX));
    }

    if (this.halted) return;

    // bullets
    for (const b of this.bullets) b.y -= BULLET_SPEED * dt;
    this.bullets = this.bullets.filter((b) => b.y > this.playTopY - 10);

    // rows: fast-forward while no live ship is on screen
    const top = this.playTopY;
    const visibleAlive = this.rows.some(
      (r) => r.y + ROW_H > top && r.ships.some((s) => s.state === "alive"),
    );
    const anyAlive = this.rows.some((r) => r.ships.some((s) => s.state === "alive"));
    const fall = this.speed * (visibleAlive || !anyAlive ? 1 : 7);
    for (const row of this.rows) {
      if (!row.ships.some((s) => s.state === "alive")) continue;
      row.y += fall * dt;
      row.x += row.vx * dt;
      if (row.x < MARGIN || row.x + row.width > LW - MARGIN) {
        row.vx = -row.vx;
        row.x = Math.max(MARGIN, Math.min(LW - MARGIN - row.width, row.x));
      }
    }

    // collisions
    for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const b = this.bullets[bi];
      let hit = false;
      for (const row of this.rows) {
        if (row.y < top - 4) continue;
        if (b.y < row.y || b.y > row.y + ROW_H) continue;
        for (const s of row.ships) {
          if (s.state !== "alive") continue;
          const x0 = row.x + s.ox;
          if (b.x >= x0 - 3 && b.x <= x0 + s.w + 3) {
            this.bullets.splice(bi, 1);
            this.resolveHit(row, s);
            hit = true;
            break;
          }
        }
        if (hit || this.halted) break;
      }
      if (this.halted) return;
    }

    // ships reaching the line
    for (const row of this.rows) {
      if (row.y + ROW_H < this.dangerY) continue;
      for (const s of row.ships) {
        if (s.state !== "alive") continue;
        this.resolvePass(row, s);
        if (this.halted) return;
      }
    }

    if (!anyAlive && !this.clearedSent && this.rows.length && this.time - this.lastResolve > 0.9) {
      this.clearedSent = true;
      this.events.onCleared();
    }
  }

  // ---- drawing -----------------------------------------------------------

  private sprite(pattern: string[], x: number, y: number, px: number) {
    const ctx = this.ctx;
    for (let r = 0; r < pattern.length; r++) {
      const line = pattern[r];
      for (let c = 0; c < line.length; c++) {
        if (line[c] === "X") ctx.fillRect(x + c * px, y + r * px, px + 0.3, px + 0.3);
      }
    }
  }

  private drawShip(s: EShip, x: number, y: number, alpha: number) {
    const ctx = this.ctx;
    const frame = Math.floor(this.time * 2.2 + s.ox * 0.01) % 2 === 0 ? INV_A : INV_B;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = SHIP_COLOR;
    this.sprite(frame, x + (s.w - SHIP_W) / 2, y, SHIP_PX);
    if (s.def.label) {
      const py = y + SHIP_H + LABEL_GAP;
      ctx.fillStyle = "rgba(6,10,26,0.92)";
      ctx.fillRect(x, py, s.w, PLATE_H);
      ctx.strokeStyle = "rgba(197,139,255,0.55)";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x + 0.75, py + 0.75, s.w - 1.5, PLATE_H - 1.5);
      ctx.fillStyle = "#ffffff";
      ctx.font = LABEL_FONT;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(s.def.label, x + s.w / 2, py + PLATE_H / 2 + 1);
    }
    ctx.globalAlpha = 1;
  }

  private draw() {
    const ctx = this.ctx;
    const scale = this.canvas.width / LW;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#05060c";
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    const sx = this.shakeT > 0 ? (Math.random() - 0.5) * 12 * (this.shakeT / 0.35) : 0;
    const sy = this.shakeT > 0 ? (Math.random() - 0.5) * 8 * (this.shakeT / 0.35) : 0;
    ctx.setTransform(scale, 0, 0, scale, sx * scale, sy * scale);
    ctx.imageSmoothingEnabled = false;

    // stars
    for (const st of this.stars) {
      const y = ((st.y * this.lh + (this.reduced ? 0 : this.time * 6 * st.s)) % this.lh + this.lh) % this.lh;
      const tw = 0.35 + 0.35 * Math.sin(this.time * 1.6 + st.p);
      ctx.fillStyle = `rgba(190,210,255,${tw.toFixed(3)})`;
      ctx.fillRect(st.x, y, st.s, st.s);
    }

    if (this.mode === "play") {
      // danger line
      ctx.strokeStyle = "rgba(54,200,255,0.35)";
      ctx.lineWidth = 2;
      ctx.setLineDash([14, 10]);
      ctx.beginPath();
      ctx.moveTo(MARGIN / 2, this.dangerY);
      ctx.lineTo(LW - MARGIN / 2, this.dangerY);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const top = this.mode === "play" ? this.playTopY : 0;
    for (const row of this.rows) {
      for (const s of row.ships) {
        if (s.state === "alive") {
          if (this.mode === "play") {
            const a = (row.y - (top - ROW_H * 0.6)) / (ROW_H * 0.6);
            if (a <= 0) continue;
            this.drawShip(s, row.x + s.ox, row.y, Math.min(1, a));
          } else {
            this.drawShip(s, row.x + s.ox, row.y, 0.9);
          }
        } else if (s.state === "warp" && s.t < 0.7) {
          const k = s.t / 0.7;
          const wob = Math.sin(this.time * 40) * 10 * (1 - k);
          this.drawShip(s, s.x + wob, s.y - 46 * k, 1 - k);
          ctx.fillStyle = `rgba(127,232,255,${(0.5 * (1 - k)).toFixed(3)})`;
          ctx.fillRect(s.x - 6, s.y - 46 * k + SHIP_H / 2 - 1, s.w + 12, 2);
        } else if (s.state === "pass" && s.t < 0.5) {
          this.drawShip(s, s.x, s.y + 30 * (s.t / 0.5), 1 - s.t / 0.5);
        } else if (s.state === "breach" && s.t < 0.5) {
          this.drawShip(s, s.x, s.y + 20 * (s.t / 0.5), 1 - s.t / 0.5);
        }
      }
    }

    // bullets
    ctx.fillStyle = "#fff4a8";
    for (const b of this.bullets) ctx.fillRect(b.x - 2, b.y - 14, 4, 14);

    // particles
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, 1 - p.life / p.max);
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx.globalAlpha = 1;

    // player
    if (this.mode === "play") {
      ctx.fillStyle = PLAYER_COLOR;
      this.sprite(CANNON, this.playerX - CANNON_W / 2, this.playerY, CANNON_PX);
    }

    // floating score
    ctx.font = FLOAT_FONT;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const f of this.floaters) {
      ctx.globalAlpha = Math.max(0, 1 - f.t / 1.3);
      ctx.fillStyle = "rgba(0,0,0,0.7)";
      ctx.fillText(f.text, f.x + 2, f.y + 2);
      ctx.fillStyle = f.color;
      ctx.fillText(f.text, f.x, f.y);
    }
    ctx.globalAlpha = 1;
  }
}
